import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { RollsetPreparedRow } from "../models/RollsCartridge";
import {
	findRollsets,
	getAvailableCartridges,
	getBarrelLayout,
	getCartridgeOptions,
	getCommonStands,
	getRollsetById,
	getRollsetsPrepared,
	prepareRollForLine,
	undoPrepareRollForLine,
} from "../repositories/RollsCartridgeRepository";

const REFETCH_INTERVAL_MS = 15_000;

export const useRollsetsPrepared = () =>
	useQuery({
		queryKey: ["rollsCartridge", "rollsetsPrepared"],
		queryFn: ({ signal }) => getRollsetsPrepared({ signal }),
		refetchInterval: REFETCH_INTERVAL_MS,
		refetchOnWindowFocus: false,
	});

/** Minimum characters before the rollset typeahead hits the server. */
export const ROLLSET_SEARCH_MIN_LENGTH = 2;

export const useRollsetSearch = (search: string) =>
	useQuery({
		queryKey: ["rollsCartridge", "rollsetSearch", search],
		queryFn: ({ signal }) => findRollsets({ search, signal }),
		enabled: search.length >= ROLLSET_SEARCH_MIN_LENGTH,
		staleTime: 60 * 1000,
		refetchOnWindowFocus: false,
	});

export const useBarrelLayout = (rollsetId: number | null) => {
	return useQuery({
		queryKey: ["rollsCartridge", "barrelLayout", rollsetId],
		queryFn: ({ signal }) =>
			getBarrelLayout({ rollsetId: rollsetId as number, signal }),
		enabled: rollsetId != null && rollsetId > 0,
		refetchOnWindowFocus: false,
	});
};

export const useRollsetDetail = (rollsetId: number | null) => {
	return useQuery({
		queryKey: ["rollsCartridge", "rollsetDetail", rollsetId],
		queryFn: ({ signal }) =>
			getRollsetById({ rollsetId: rollsetId as number, signal }),
		enabled: rollsetId != null && rollsetId > 0,
		refetchOnWindowFocus: false,
	});
};

export const useCommonStands = () => {
	return useQuery({
		queryKey: ["rollsCartridge", "commonStands"],
		queryFn: ({ signal }) => getCommonStands({ signal }),
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
	});
};

export const useCartridgeOptions = () => {
	return useQuery({
		queryKey: ["rollsCartridge", "cartridgeOptions"],
		queryFn: ({ signal }) => getCartridgeOptions({ signal }),
		// Which cartridge holds which rollset changes with every preparation,
		// so this list follows the grid's refresh rather than sitting cached.
		refetchInterval: REFETCH_INTERVAL_MS,
		refetchOnWindowFocus: false,
	});
};

export const useAvailableCartridges = (millSizeId?: number | null) => {
	return useQuery({
		queryKey: ["rollsCartridge", "availableCartridges", millSizeId ?? null],
		queryFn: ({ signal }) => getAvailableCartridges({ millSizeId, signal }),
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
	});
};

interface MutationCallbacks {
	onSuccess?: () => void;
	onError?: (err: Error) => void;
}

export const usePrepareRollForLine = ({
	onSuccess,
	onError,
}: MutationCallbacks = {}) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: prepareRollForLine,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["rollsCartridge", "rollsetsPrepared"],
			});
			queryClient.invalidateQueries({
				queryKey: ["rollsCartridge", "rollsetDetail"],
			});
			onSuccess?.();
		},
		onError: (err: Error) => onError?.(err),
	});
};

export const useUndoPrepareRollForLine = ({
	onSuccess,
	onError,
}: MutationCallbacks = {}) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: undoPrepareRollForLine,
		onSuccess: (_data, variables) => {
			const rollsetId = variables.rollsetId;

			// Optimistically drop the undone rollset from any cached "prepared"
			// list so the master grid hides the row instantly. The detail
			// panel's Op. Status is driven by local state in the container
			// (see opStatusOverride), which is the authoritative post-mutation
			// source — immune to cross-DB drift when the detail proxy reads
			// from a different DB than the mutation wrote to.
			queryClient.setQueriesData<RollsetPreparedRow[]>(
				{ queryKey: ["rollsCartridge", "rollsetsPrepared"] },
				(old) => {
					if (!old) return old;
					return old.filter((r) => r.Id !== rollsetId);
				},
			);

			queryClient.invalidateQueries({
				queryKey: ["rollsCartridge", "rollsetsPrepared"],
			});
			queryClient.invalidateQueries({
				queryKey: ["rollsCartridge", "rollsetDetail"],
			});
			onSuccess?.();
		},
		onError: (err: Error) => onError?.(err),
	});
};
